import isObject from 'lodash-es/isObject'
import isFinite from 'lodash-es/isFinite'
import { ClientlessAPI } from './ClientlessAPI'
import { IAuthEngine, PresentRegCodeResult } from './IAuthEngine'
import {
  AuthorizationDataResponse,
  AuthStatusResponse,
  GetSelectedProviderResponse,
  MetadataDataResponse,
  AuthEngineResponseType,
  PreauthorizationDataResponse,
} from './ClientlessAuthEngineResponse'
import { MetadataKey, MetadataArgsMap } from './engine/AuthEngineTypes'
import {
  isAuthError,
  AuthErrorBuilder,
  AuthErrorSeverity,
  AuthErrorCategory,
  AuthErrorDomain,
  AuthErrorNumeral,
} from './AuthError'
import { AuthConfiguration } from './auth/common/configuration/AuthConfiguration'
import { DeviceInfoService } from './web/DeviceInfoService'
import {
  Provider,
  MVPDConfigAPI,
  MVPDConfiguration,
  ImageTemplateBuilderData,
} from './services/config/MVPDConfigAPI'
import { Deferred } from './util/Deferred'
import { Timer, TimerMode } from './util/Timer'
import { base64ToUtf8 } from './util/crypto'
import { MvpdType } from './api-clients/rest-api-v2/types.gen'

interface PollerResult {
  promise: Promise<boolean>
  cancel: () => void
}

const SECOND = 1000
const MINUTE = 60 * SECOND

/**
 * @hidden -- exported publicly but not intended for public consumption
 */
export class ClientlessAuthEngine implements IAuthEngine {
  authConfig: AuthConfiguration
  private isInAuthenticationFlow = false
  cachedUserGuid?: string
  private _ready: Deferred<boolean> = new Deferred()
  ready: Promise<boolean> = this._ready.promise
  private _configService?: MVPDConfigAPI
  private readonly _deviceInfoService: DeviceInfoService
  private _clientlessApi?: ClientlessAPI
  private _mvpdConfig?: MVPDConfiguration
  private _pollingInterval: number = 30 * SECOND
  private _poller?: PollerResult

  constructor(authConfig: AuthConfiguration) {
    this.authConfig = authConfig
    this._deviceInfoService = new DeviceInfoService(authConfig)
  }

  async prepare(): Promise<AuthStatusResponse> {
    const {
      environment,
      brand,
      softwareStatement,
      redirectUri,
      pollingInterval,
      ecid
    } = this.authConfig
    const MIN_POLLING_INTERVAL = 10 * SECOND
    const MAX_POLLING_INTERVAL = 2 * MINUTE

    // Clamp pollingInterval (inline since no lodash-es/clamp in lite)
    if (pollingInterval && isFinite(pollingInterval)) {
      const intervalMs = pollingInterval * SECOND
      this._pollingInterval = Math.max(MIN_POLLING_INTERVAL, Math.min(intervalMs, MAX_POLLING_INTERVAL))
    }

    // Initialize device info service once - this fetches device ID, hashes it,
    // and pre-computes device info strings. After this, all getters are synchronous.
    await this._deviceInfoService.init()

    this._clientlessApi = new ClientlessAPI({
      adobeEnv: environment,
      deviceInfoService: this._deviceInfoService,
      ecid,
      redirectUri,
      requestorId: brand,
      softwareStatement,
    })

    await this._clientlessApi.runDynamicClientRegistration()

    // Initialize config service
    this._configService = new MVPDConfigAPI(
      this.authConfig.mvpdConfigURL,
      brand,
      this.authConfig.platform
    )

    // Fetch internal MVPD config
    let internalMvpdConfig: MVPDConfiguration
    try {
      internalMvpdConfig = await this._configService.fetchConfig()
    } catch (error) {
      console.error('[prepare] Internal MVPD config fetch failed:', error)
      return { type: AuthEngineResponseType.Did_Set_Authentication_Status, isAuthenticated: false, info: 'MVPD config fetch failed', shouldLogoutInSettings: false }
    }

    // Now let's fetch Adobe's config and merge with ours.
    // Guard against stale/invalid DCR credentials (e.g., V1 client_id when using V2 APIs)
    let theirMvpds: MvpdType[]
    try {
      theirMvpds = await this._clientlessApi.requestConfig()
    } catch (error) {
      // If we get a 401 Unauthorized, it likely means the stored DCR credentials are invalid
      // (e.g., from a previous run with different software statement or API version)
      if (isAuthError(error) && error.message.includes('401')) {
        // Clear the invalid stored credentials and get new ones from adobe.
        console.warn('[setRequestor] DCR credentials were rejected. Fetching new ones.')
        await this._clientlessApi.clearStoredDCRCredentials()
        await this._clientlessApi.runDynamicClientRegistration()
        theirMvpds = await this._clientlessApi.requestConfig()
      } else {
        // Not a 401, re-throw the original error
        throw error
      }
    }

    const { mvpd: ourMvpds } = internalMvpdConfig

    this._mvpdConfig = {
      ...internalMvpdConfig,
      mvpd: mergeMVPDLists(ourMvpds, theirMvpds)
    }

    this._ready.resolve(true)

    // lite-auth IAuthEngine.prepare() returns AuthStatusResponse
    const response: AuthStatusResponse = {
      type: AuthEngineResponseType.Did_Set_Authentication_Status,
      isAuthenticated: false,
      info: '',
      shouldLogoutInSettings: false
    }

    return response
  }

  async checkAuthenticatedStatus(): Promise<AuthStatusResponse> {
    console.debug('[checkAuthenticatedStatus] called...')

    await this.ready
    if (!this._clientlessApi) return buildAuthEngineError()

    const response = {
      type: AuthEngineResponseType.Did_Set_Authentication_Status,
      info: '',
      shouldLogoutInSettings: false,
    }

    const profile = await this._clientlessApi.requestAuthNProfile()

    if (!profile) {
      console.debug('[checkAuthenticatedStatus] No profiles received. User is not authenticated.')
      this.cachedUserGuid = undefined

      return {
        ...response,
        isAuthenticated: false,
      }
    }

    try {
      // the point of this is to make sure the user is authed with an MVPD that is also in our list.
      await this.getSelectedProvider()
    } catch (_) {
      console.warn('[checkAuthenticatedStatus] The user has signed in with an MVPD that is not in our MVPD config list! Signing out now.')
      return this.logout() // same object as we were going to return from this function (i.e. isAuthed false object)
    }

    // Update our cached ID.
    this.cachedUserGuid = this._clientlessApi.getUserID() || undefined

    console.debug('[checkAuthenticatedStatus] User is authenticated')

    return {
      ...response,
      isAuthenticated: true,
    }
  }

  private _getMvpdConfigEntry(mvpdId: string): Provider | undefined {
    if (!this._mvpdConfig) {
      console.error('Cannot continue without an MVPD config!')
      return
    }

    return this._mvpdConfig.mvpd.find(provider => provider.id === mvpdId)
  }

  async getSelectedProvider(): Promise<GetSelectedProviderResponse> {
    await this.ready
    if (!this._clientlessApi) return buildAuthEngineError()

    const mvpd = this._clientlessApi.getMvpdID()
    console.debug(`[getSelectedProvider] MVPD: ${mvpd}`)

    if (mvpd) {
      const provider = this._getMvpdConfigEntry(mvpd)
      console.debug(`[getSelectedProvider] Found "${mvpd}" in config: ${provider ? 'true' : 'false'}`)

      if (provider) {
        return {
          type: AuthEngineResponseType.Did_Receive_Selected_Provider,
          provider
        }
      }
    }

    // FIXME: check if `null` is a valid response, or should we reject?
    return Promise.reject(null)
  }

  async login(presentRegCodeCallback: (data: PresentRegCodeResult) => void): Promise<AuthStatusResponse> {
    console.debug(`[login] called...`)

    await this.ready
    if (!this._clientlessApi) return buildAuthEngineError()

    const check = await this.checkAuthenticatedStatus()

    console.debug('[login] check:', check)

    if (check.isAuthenticated) {
      console.debug('[login] already logged in, short-circuiting...')
      return check
    }

    const code = await this._clientlessApi.requestRegCode()

    console.info(`[login] Received RegCode: ${code}`)

    // Emit regcode via callback
    if (presentRegCodeCallback && this._mvpdConfig) {
      presentRegCodeCallback({
        registrationCode: code,
        registrationURL: this.authConfig.registrationURL || '',
        helpURL: this.authConfig.helpURL || '',
      })
    }

    this.isInAuthenticationFlow = true
    if (this._poller) this._poller.cancel()
    const poller: PollerResult = this.pollForAuthNSuccess(code.slice(0, -1)) // pop off the '2'
    const { promise } = poller
    this._poller = poller
    const result = {
      type: AuthEngineResponseType.Did_Set_Authentication_Status,
      isAuthenticated: false,
      info: '',
      shouldLogoutInSettings: false
    }
    try {
      await promise
      result.isAuthenticated = true
    } catch (error) {
      result.isAuthenticated = false
      if (isAuthError(error)) {
        const authError = error
        result.info = `[${authError.code}] ${authError.message}`
      }
      return Promise.reject(error)
    } finally {
      this._poller = undefined
      this.isInAuthenticationFlow = false
    }
    const castResult: AuthStatusResponse = { ...result }
    return castResult
  }

  cancelAuthentication(): void {
    if (this.isInAuthenticationFlow && this._poller) {
      this._poller.cancel()
      this.isInAuthenticationFlow = false
    }
  }

  async authorize(channel: string): Promise<AuthorizationDataResponse> {
    console.debug('[authorize] called... channel:', channel)

    await this.ready
    if (!this._clientlessApi) return buildAuthEngineError()

    // Validate channel parameter
    if (!channel) {
      const error = new AuthErrorBuilder()
        .withMessage('TypeError: A valid channel is required for "authorize"')
        .withCategory(AuthErrorCategory.Authorization)
        .withDomain(AuthErrorDomain.Shared)
        .withNumeral(AuthErrorNumeral.TypeError)
        .build()
      console.warn('[authorize] error:', error)
      return Promise.reject(error)
    }

    // Check if authenticated
    const authStatus = await this.checkAuthenticatedStatus()
    if (!authStatus.isAuthenticated) {
      const error = new AuthErrorBuilder()
        .withMessage('Not Authenticated')
        .withCategory(AuthErrorCategory.Authorization)
        .withDomain(AuthErrorDomain.Shared)
        .withNumeral(AuthErrorNumeral.NotAuthenticated)
        .withRecoverySuggestion('The user must be authenticated before authorization')
        .build()
      console.warn('[authorize] error:', error)
      return Promise.reject(error)
    }
    const { decisions } = await this._clientlessApi.authorize(channel)

    const token = decisions?.[0].token

    if (!token) {
      return Promise.reject('Missing \'token\' data')
    }

    const { serializedToken } = token
    console.debug('[authorize] serializedToken:', serializedToken)

    if (!serializedToken) {
      return Promise.reject('Missing \'serializedToken\'')
    }

    let decodedToken
    try {
      decodedToken = base64ToUtf8(serializedToken)
    } catch (error) {
      console.debug('[authorize] failed to decode serialized token:', error)
      // leave decodedToken undefined
    }

    console.debug('[authorize] decodedToken:', decodedToken)

    const result: AuthorizationDataResponse = {
      type: AuthEngineResponseType.Did_Set_Token,
      channel,
      token: decodedToken || serializedToken
    }
    return result
  }

  // This preauthorize is meant to be used from the client app, not from the second screen regcode app. The REST API
  // call doesn't accept caching options like the AccessEnabler does.
  async preauthorize(channels: string[]): Promise<PreauthorizationDataResponse> {
    console.debug('[preauthorize] called...')

    await this.ready
    if (!this._clientlessApi) return buildAuthEngineError()

    // Validate channels parameter
    if (!channels?.length) {
      const error = new AuthErrorBuilder()
        .withMessage('TypeError: Preauthorize must be called with an array of channels')
        .withCategory(AuthErrorCategory.Preauthorization)
        .withDomain(AuthErrorDomain.Shared)
        .withNumeral(AuthErrorNumeral.TypeError)
        .build()
      console.warn('[preauthorize] error:', error)
      return Promise.reject(error)
    }

    // Check if authenticated
    const authStatus = await this.checkAuthenticatedStatus()
    if (!authStatus.isAuthenticated) {
      const error = new AuthErrorBuilder()
        .withMessage('Not Authenticated. Preauthorization requires authentication.')
        .withCategory(AuthErrorCategory.Preauthorization)
        .withDomain(AuthErrorDomain.Shared)
        .withNumeral(AuthErrorNumeral.NotAuthenticated)
        .build()
      console.warn('[preauthorize] error:', error)
      return Promise.reject(error)
    }

    const response = await this._clientlessApi.requestPreauthorizedResources(channels)
    const result: PreauthorizationDataResponse = {
      type: AuthEngineResponseType.Did_Receive_Preauthorized_Resources,
      decisions: response.decisions
    }

    console.debug('[preauthorize] result:', result)

    return result
  }

  async logout(): Promise<AuthStatusResponse> {
    console.debug('[logout] called...')

    await this.ready
    if (!this._clientlessApi) return buildAuthEngineError()

    // Short-circuit if not authenticated
    if (!this._clientlessApi.getMvpdID()) {
      console.debug('[logout] not authenticated, short-circuiting...')
      return {
        type: AuthEngineResponseType.Did_Set_Authentication_Status,
        isAuthenticated: false,
        info: '',
        shouldLogoutInSettings: false
      }
    }

    // logout flow
    this.isInAuthenticationFlow = true
    try {
      const response = await this._clientlessApi.logout()
      console.debug('[logout] response:', response)

      const result: AuthStatusResponse = {
        type: AuthEngineResponseType.Did_Set_Authentication_Status,
        isAuthenticated: false,
        info: '',
        shouldLogoutInSettings: false
      }

      // Verify logout was successful
      if (result.isAuthenticated) {
        const error = new AuthErrorBuilder()
          .withMessage('Logout Failed')
          .withCategory(AuthErrorCategory.Logout)
          .withDomain(AuthErrorDomain.Adobe)
          .withNumeral(AuthErrorNumeral.AdobeLogoutFailure)
          .withSubErrorMessage('Authentication status was not invalidated post-logout')
          .build()
        console.warn('[logout] error:', error)
        return Promise.reject(error)
      }

      // todo: To ACTUALLY log the user out of their MVPD we need to see if actionType is interactive, and navigate to
      // the url if it is. Until then, we have a known issue: Clientless users can sign out, but if they try to sign
      // back in with the SAME MVPD, they may be auto-logged in by them.

      return result
    } catch (error) {
      return Promise.reject(error)
    } finally {
      // ensure these are cleared
      this.cachedUserGuid = undefined
      this.isInAuthenticationFlow = false
    }
  }

  // NOTE: This is ONLY called when authenticated. Will error before then if we dont have a profile.
  async fetchMetadata(key: MetadataKey, args?: MetadataArgsMap): Promise<MetadataDataResponse> {
    if (!key) return buildFetchMetadataTypeError()
    // Depending upon the key, you have to fetch from different sources.
    switch (key) {
      case MetadataKey.DEVICE_ID:
        return this._getDeviceIdMetadata()
      case MetadataKey.AUTHENTICATED_TTL:
        return this._fetchAuthNTTLMetadata()
      case MetadataKey.AUTHORIZED_TTL: {
        let requestedResource = ''
        if (isObject(args)) {
          const argsObj = args as Record<string, any>
          const { channel, resource, resourceId, resourceID } = argsObj
          requestedResource = channel || resource || resourceId || resourceID || ''
        }
        return this._fetchAuthZTTLMetadata(requestedResource)
      }
      default:
        // ASSUME: only authZ TTL uses `args`
        return this._fetchUserMetadata(key)
    }
  }

  buildImageURL(data: ImageTemplateBuilderData): string {
    if (!this._configService) {
      throw new Error('Config service not initialized')
    }
    return this._configService.buildImageURLString(data)
  }

  private _getDeviceIdMetadata(): MetadataDataResponse {
    const deviceId = this._deviceInfoService.getHashedDeviceId() || null
    const result: MetadataDataResponse = {
      type: AuthEngineResponseType.Did_Set_Metadata_Status,
      key: MetadataKey.DEVICE_ID,
      encrypted: false,
      value: deviceId
    }
    return result
  }

  private async _fetchAuthNTTLMetadata(): Promise<MetadataDataResponse> {
    if (!this._clientlessApi) return buildAuthEngineError()
    const value: number | null = this._clientlessApi.getAuthNTTL()
    const result: MetadataDataResponse = {
      type: AuthEngineResponseType.Did_Set_Metadata_Status,
      key: MetadataKey.AUTHENTICATED_TTL,
      encrypted: false,
      value
    }
    return result
  }

  private async _fetchAuthZTTLMetadata(resource = ''): Promise<MetadataDataResponse> {
    if (!this._clientlessApi) return buildAuthEngineError()
    const value: number | null = this._clientlessApi.getAuthZTTL(resource)
    const result: MetadataDataResponse = {
      type: AuthEngineResponseType.Did_Set_Metadata_Status,
      key: MetadataKey.AUTHORIZED_TTL,
      encrypted: false,
      resource,
      value
    }
    return result
  }

  // note: no longer actually fetches anything. it's all read from the profile which must exist already
  private async _fetchUserMetadata(key: MetadataKey): Promise<MetadataDataResponse> {
    if (!this._clientlessApi) return buildAuthEngineError()
    const { encrypted = false, value = null } = this._clientlessApi.requestUserMetadataEntry(key) ?? {}
    const result: MetadataDataResponse = {
      type: AuthEngineResponseType.Did_Set_Metadata_Status,
      key,
      encrypted,
      value
    }
    return result
  }

  /**
   * ### Poll for AuthN
   *
   * Polling checkAuthNToken until we get a success
   */
  private pollForAuthNSuccess(code: string): PollerResult {
    if (!this._clientlessApi) throw new TypeError('Clientless API is not available')
    const deferred = new Deferred<boolean>()
    const TIMEOUT_MS = 15 * MINUTE
    const pollingTimer = Timer(
      TimerMode.Interval,
      async () => {
        if (!this._clientlessApi) {
          stop()
          console.error('[Polling] Clientless API is not available')
          return
        }
        console.debug('[Polling] Awaiting authN success...')
        try {
          if (await this._clientlessApi.checkRegCodeAuthN(code)) {
            console.debug('[Polling] Obtained authN success...')
            deferred.resolve(true)
            stop()
          }
        } catch (error) {
          deferred.reject(error)
          stop()
        }
      },
      this._pollingInterval
    )
    const stop = () => {
      pollingTimer.stop()
      timeoutTimer.stop()
    }
    const cancel = (reason: AuthErrorNumeral) => () => {
      stop()
      deferred.reject(
        new AuthErrorBuilder()
          .withMessage('Canceled authentication')
          .withCategory(AuthErrorCategory.Authentication)
          .withDomain(AuthErrorDomain.Shared)
          .withNumeral(reason)
          .build()
      )
    }
    const timeoutTimer = Timer(
      TimerMode.Timeout,
      cancel(AuthErrorNumeral.AuthenticationTimeout),
      TIMEOUT_MS
    )
    pollingTimer.start()
    timeoutTimer.start()
    const result: PollerResult = {
      promise: deferred.promise,
      cancel: cancel(AuthErrorNumeral.ClientlessCheckAuthNCanceled)
    }
    return result
  }
}

const buildAuthEngineError = () => Promise.reject(new AuthErrorBuilder()
  .withMessage('Auth Engine is missing or was disposed')
  .withSeverity(AuthErrorSeverity.Fatal)
  .withCategory(AuthErrorCategory.Initialization)
  .withDomain(AuthErrorDomain.Target)
  .withNumeral(AuthErrorNumeral.ClientlessAPIUninitialized)
  .withRecoverySuggestion('setRequestor() must be called first')
  .build())

const buildFetchMetadataTypeError = () => Promise.reject(new AuthErrorBuilder()
  .withMessage('TypeError: A valid key param is required for "fetchMetadata"')
  .withCategory(AuthErrorCategory.Metadata)
  .withDomain(AuthErrorDomain.Runtime)
  .withNumeral(AuthErrorNumeral.TypeError)
  .build())

/**
 * Merge Adobe's MVPD list with our MVPDConfig allowlist.
 * Copied from top-auth ClientlessAuthEngine.ts to ensure identical behavior
 * @hidden
 */
export const mergeMVPDLists = (mvpdConfigMVPDs: Provider[], adobeConfigMVPDs: MvpdType[]): Provider[] => {
  if (!adobeConfigMVPDs?.length) return mvpdConfigMVPDs

  // A look-up table of MVPD IDs in Adobe's config
  const adobeConfigMvpdMap = adobeConfigMVPDs.reduce(
    (memo, mvpd) => {
      if (mvpd.id)
        memo[mvpd.id] = mvpd
      return memo
    },
    {} as Record<string, MvpdType>,
  )

  // Looping through our MVPD Config, merging with Adobe's provider data, if available
  const newProviders = mvpdConfigMVPDs
    .map((mvpd: Provider) => {
      const { id } = mvpd
      // It's possible a provider in our config does not exist in Adobe's config
      if (id in adobeConfigMvpdMap) {
        const adobeData = adobeConfigMvpdMap[id]
        // Overwrite Adobe's config entry with our config entry -- just need Adobe's extra props
        const provider = { ...adobeData, ...mvpd }
        delete provider.logoUrl
        return provider
      }
      return undefined // Explicitly return undefined for providers not in Adobe's config
    })
    // Filter out the `undefined`s from skipping any IDs not in MVPD Config
    .filter(Boolean) as Provider[]

  return newProviders
}
